import React, { useEffect, useState } from 'react';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
import { equalShares } from '../../utils/progress';
import { performersOf } from '../../utils/today';
import { formatTimeOfDay } from '../../utils/time';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Avatar } from '../Avatar';
export function ApprovalSheet() {
  const {
    approvalId,
    openApproval
  } = useUi();
  const {
    templateById,
    getOcc,
    memberById,
    approve,
    requestCorrection,
    isAdmin
  } = useAppState();
  const template = templateById(approvalId ?? undefined);
  const occ = approvalId ? getOcc(approvalId) : undefined;
  const value = template?.baseValue ?? 0;
  const performers = template && occ ? performersOf(template, occ) : [];
  const [shares, setShares] = useState<Record<string, string>>({});
  const [correcting, setCorrecting] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!template) return;
    const eq = equalShares(value, performers);
    setShares(Object.fromEntries(Object.entries(eq).map(([k, v]) => [k, String(v)])));
    setCorrecting(false);
    setNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, value, performers.join(',')]);
  const close = () => openApproval(null);
  const open = Boolean(template && occ && isAdmin && occ.status === 'v_pregledu');
  const parsed = Object.fromEntries(Object.entries(shares).map(([k, v]) => [k, Number.parseInt(v, 10)]));
  const sum = Object.values(parsed).reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
  const sharesValid = Object.values(parsed).every(v => Number.isFinite(v) && v >= 0) && sum === value;
  const multi = performers.length > 1;
  return <Sheet open={open} onClose={close} title={template?.title ?? ''} subtitle={occ?.submittedBy ? `Oddal(a) ${memberById(occ.submittedBy)?.name ?? ''}${occ.submittedAt ? ` · ob ${formatTimeOfDay(occ.submittedAt)}` : ''}` : undefined} footer={correcting ? <div className="grid grid-cols-2 gap-2" data-id="element-493" data-mp="1er3f78ff46th">
            <Button variant="secondary" onClick={() => setCorrecting(false)} data-id="element-494" data-mp="10ydst2vz8zvu">
              Nazaj
            </Button>
            <Button disabled={!note.trim()} onClick={() => {
      if (approvalId) requestCorrection(approvalId, note.trim());
      close();
    }} data-id="element-495" data-mp="14yxpy6gjw3hz">
              Pošlji popravek
            </Button>
          </div> : <div className="grid grid-cols-2 gap-2" data-id="element-496" data-mp="1jpen3dm5k792">
            <Button variant="secondary" onClick={() => setCorrecting(true)} data-id="element-497" data-mp="1jqy5b9rdcsdh">
              Zahtevaj popravek
            </Button>
            <Button variant="success" disabled={!sharesValid} onClick={() => {
      if (approvalId) approve(approvalId, parsed);
      close();
    }} data-id="element-498" data-mp="88x34czqrx3b">
              Potrdi
            </Button>
          </div>} data-id="element-492" data-mp="3gnkm513aav7v">
      {template && !correcting && <div data-id="element-499" data-mp="pnu4kv5e0es">
          <div className="flex items-baseline justify-between" data-id="element-500" data-mp="s9zcdukg3tu">
            <h3 className="text-sm font-extrabold" data-id="element-501" data-mp="eodss4xm8859">{multi ? 'Izvajalci in deleži točk' : 'Izvajalec'}</h3>
            <span className="text-sm font-semibold text-muted" data-id="element-502" data-mp="9r54dmlvj7vk">Skupaj {value} točk</span>
          </div>
          <ul className="mt-2 divide-y divide-line rounded-2xl border border-line" data-id="element-503" data-mp="3i6ypker5h7iy">
            {performers.map(id => {
          const m = memberById(id);
          if (!m) return null;
          return <li key={id} className="flex items-center gap-3 px-3 py-2.5" data-id="element-504" data-mp="3ry1phd8cfggn">
                  <Avatar member={m} size="sm" data-id="element-505" data-mp="3n4784gg7ndkq" />
                  <label htmlFor={`share-${id}`} className="flex-1 font-bold" data-id="element-506" data-mp="2zfe5601685gm">
                    {m.name}
                  </label>
                  <input id={`share-${id}`} type="number" inputMode="numeric" min={0} max={value} value={shares[id] ?? ''} onChange={e => setShares(s => ({
              ...s,
              [id]: e.target.value
            }))} disabled={!multi} className="h-10 w-20 rounded-lg border border-line bg-surface px-2 text-right font-extrabold tabular-nums focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 disabled:bg-canvas" data-id="element-507" data-mp="2ugbczg6ahtq2" />
                  <span className="w-10 text-sm text-muted" data-id="element-508" data-mp="2ld79xwah4m9c">točk</span>
                </li>;
        })}
          </ul>
          {multi && <div className="mt-2 flex items-center justify-between text-xs" data-id="element-509" data-mp="2ug8cxo41lx8s">
              <span className={sharesValid ? 'text-muted' : 'font-semibold text-accent-ink'} data-id="element-510" data-mp="w03vzvrbj7eg">
                {sharesValid ? 'Vsota se ujema s skupno vrednostjo.' : `Vsota je ${sum}, mora biti ${value}.`}
              </span>
              <button type="button" className="font-bold text-info-ink underline-offset-2 hover:underline" onClick={() => {
          const eq = equalShares(value, performers);
          setShares(Object.fromEntries(Object.entries(eq).map(([k, v]) => [k, String(v)])));
        }} data-id="element-511" data-mp="sig2lwqn833t">
                Razdeli enako
              </button>
            </div>}
          <p className="mt-4 text-xs text-muted" data-id="element-512" data-mp="1bey1qz7tn5a3">
            Potrditev velja za celotno opravilo, brez kontrolnih podtočk. Točke dobijo dejanski izvajalci; po potrditvi se
            ne morejo šteti dvakrat.
          </p>
        </div>}
      {correcting && <div data-id="element-513" data-mp="xghmukzt28gv">
          <label htmlFor="correction-note" className="text-sm font-extrabold" data-id="element-514" data-mp="xgzs274g3x0x">
            Kaj je treba popraviti?
          </label>
          <textarea id="correction-note" rows={3} autoFocus value={note} onChange={e => setNote(e.target.value)} placeholder="npr. Pult in štedilnik še nista obrisana." className="mt-1.5 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30" data-id="element-515" data-mp="3i7i3wteb2tyk" />
          <p className="mt-1 text-xs text-muted" data-id="element-516" data-mp="3eot2yzvtplay">Kratka opomba je obvezna. Otrok jo vidi pri opravilu.</p>
        </div>}
    </Sheet>;
}